import React, { Fragment, useState } from "react";
import {
  Button,
  useDataProvider,
  useMutation,
  useNotify,
  useRedirect,
} from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Typography
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import { useFormState } from "react-final-form";
import moment from "moment/moment";
import { fetchAllData } from "../../../dataProvider";

interface IEditButtonExcludeBusAssistant {
  formInvalid: boolean;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

const SaveButtonExcludeBusAssistant = ({formInvalid}: IEditButtonExcludeBusAssistant) => {
  const [open, setOpen] = useState<boolean>(false);
  const [subcontractorName, setSubcontractorName] = useState<string>("");
  const [selectedRouteNames, setSelectedRouteNames] = useState<string[]>([]);
  const [routeNotInTimesheet, setRouteNotInTimesheet] = useState<string[]>([]);

  const notify = useNotify();
  const redirect = useRedirect();
  const classes = useStyles();
  const provider = useDataProvider();

  const { values } = useFormState({ subscription: { values: true } });

  const [saveExcludeBusAssistant, { loading: saveLoading }] = useMutation(
    {
      type: "create",
      resource: "excludeBusAssistants",
      payload: {
        data: {
          schoolId: values.schoolId,
          subcontractorId: values.subcontractorId,
          busScheduleId: values.busScheduleId,
          routeIds: values.routeIds,
          month: values.month,
          year: values.year,
        },
      },
    },
    {
      onSuccess: ({ res }) => {
        notify(`Success Exclude Bus Assistant for route(s): ${selectedRouteNames.join(", ")}`, "info");
        redirect("/excludeBusAssistants");
      },
      onFailure: (error) => {
        notify(
          `Failed Exclude Bus Assistant for route(s): ${selectedRouteNames.join(", ")}, error: ${error.message}`,
          "error",
        );
      },
    },
  );

  const handleOpenDialog = async () => {
    try {
      // get subcontractor name
      const subcontractorRes = await provider.getOne("subcontractors", {
        id: values.subcontractorId,
      });
      setSubcontractorName(subcontractorRes.data.name);

      // get route names
      const routeRes = await provider.getMany("routes", {
        ids: values.routeIds,
      });
      const routeNames = routeRes.data.map((r: any) => r.name);
      setSelectedRouteNames(routeNames);

      // Check if the subcontractor has serve the selected route,
      // If not, show the confirmation dialog. Otherwise, save the exclude bus assistant directly
      const subcontractorRouteResponse = await fetchAllData("getLatestSubcontractorRoutes", {
        SchoolId: values.schoolId,
        SubcontractorId: values.subcontractorId,
        RouteNames: routeNames,
        Date: moment().format("Y-MM-DD"),
      }, {
        field: "Id",
        order: "desc"
      })

      const subcontractorRoutes = subcontractorRouteResponse.map((r: any) => r.routeName);
      const routeNotInSubcontractorRoutes = routeNames.filter((name) => !subcontractorRoutes.includes(name));
      setRouteNotInTimesheet(routeNotInSubcontractorRoutes);

      if (routeNotInSubcontractorRoutes.length > 0) {
        setOpen(true);
      } else {
        saveExcludeBusAssistant();
      }
    } catch (error: any) {
      notify(`Failed to get latest subcontractor routes, error: ${error.message}`, "error");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <Button
        label="Save"
        disabled={formInvalid || saveLoading}
        onClick={handleOpenDialog}
      >
        <SaveIcon />
      </Button>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Exclude Bus Assistant
        </DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            <Typography variant="body1" gutterBottom>
              Are you sure you want to exclude bus assistant for {subcontractorName} on route(s):{" "}
              <strong>{selectedRouteNames.join(", ")}</strong>?
              <br/>
              This subcontractor is not serve route(s): <strong>{routeNotInTimesheet.join(", ")}</strong>{" "}
              within last 1 year.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            label="Back"
            color="secondary"
            onClick={handleClose}
            className={classes.cancelButton}
            disabled={saveLoading}
          />
          <Button
            label="Confirm"
            onClick={saveExcludeBusAssistant}
            disabled={saveLoading}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default SaveButtonExcludeBusAssistant;